import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from '../ui/Button'; // Corrected relative path
import ArtistProfile from '@/components/ArtistProfile';
import { RosterMember, ArtistProfile as IArtistProfile, UserRole } from '@/types';

interface ProfileModalProps {
  member: RosterMember | null;
  onClose: () => void;
  onChat: (profile: IArtistProfile) => void;
  onBook: (profile: IArtistProfile) => void;
  isOwnProfile: boolean;
}

// A transformation function to create a default or mock IArtistProfile
// This is used to ensure the ArtistProfile component always gets a valid profile structure.
export const transformRosterMemberToArtistProfile = (
    member: RosterMember,
    fallbackProfile: IArtistProfile // A default structure to merge with
  ): IArtistProfile => {
    return {
      ...fallbackProfile, // Start with the fallback
      id: member.id,
      name: member.name,
      avatar: member.avatar || fallbackProfile.avatar,
      role: member.role || UserRole.ARTIST,
      description: member.bio || fallbackProfile.description,
      location: member.location || fallbackProfile.location,
      // You can add more specific transformations here if needed
    };
  };

const ProfileModal: React.FC<ProfileModalProps> = ({ member, onClose, onChat, onBook, isOwnProfile }) => {

  if (!member) return null;

  // A mock or default IArtistProfile structure
  const MOCK_ARTIST_PROFILE: IArtistProfile = {
    id: '',
    name: '',
    avatar: 'https://placehold.co/400x400',
    role: UserRole.ARTIST,
    description: 'Bio not available.',
    location: 'Location not set',
    genres: ['Genre'],
    verified: false,
    pressKit: {
      photos: [],
      topTracks: [],
      techRiderUrl: '',
      socials: [],
    },
    stats: {
        gigsCompleted: 0,
        activeGigs: 0,
        rating: 0,
        responseTime: ''
    },
    isMock: true,
    xp: 0,
    level: 0,
    coverImage: ''
  };

  // Use the transformation utility to create the profile object
  const artistProfile = transformRosterMemberToArtistProfile(member, MOCK_ARTIST_PROFILE);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col shadow-xl"
        >
          <div className="p-4 border-b border-kala-800 flex justify-between items-center flex-shrink-0">
            <h2 className="text-xl font-bold text-white">Member Profile</h2>
            <Button variant="ghost" size="icon" onClick={onClose} className="text-kala-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
          <div className="flex-grow overflow-y-auto p-2 md:p-6">
            <ArtistProfile
              artist={artistProfile}
              onChat={() => onChat(artistProfile)}
              onBook={() => onBook(artistProfile)}
              isOwnProfile={isOwnProfile}
              isBlocked={false} // This can be enhanced later if needed
              onUpdateProfile={() => {}} // Profile updates handled on a dedicated page
              initialTab="overview"
            />
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProfileModal;
